import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  Injectable,
  NgModule,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
  Title,
  TransferState,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';

import { environment } from '@env/environment';

import { translateBrowserLoaderFactory } from '@app/core/loaders/translate-browser.loader';
import {
  AmplitudeAbstractService,
  AmplitudeBrowserService,
  AppLoadService,
} from '@app/core/services';
import { BtBreadcrumbsComponent } from '@app/shared/components/bt-breadcrumbs/bt-breadcrumbs.component';
import { LazySideMapComponent } from '@app/shared/components/side-map/lazy-side-map.component';
import { SidenavComponent } from '@app/shared/components/sidenav/sidenav.component';
import { IsPlacesVisibleGuard } from '@app/shared/guards/is-place-visible.guard';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { CoreModule } from './core/core.module';
import { SHARED_INTERCEPTORS } from './shared/interceptors';

declare let Hammer: any;

const OPT_PROVIDERS = [];

export function init_app(appLoadService: AppLoadService) {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return () => appLoadService.initializeApp();
}

@Injectable()
export class BmErrorHandler implements ErrorHandler {
  // constructor() {}
  handleError(err): void {
    Sentry.captureException(err.originalError || err);
    throw err;
  }
}

if (environment.production || environment.stage) {
  Sentry.init({
    dsn: environment.SENTRY_URL,
    blacklistUrls: ['localhost', '192.168.14.200'],
  });

  OPT_PROVIDERS.push({
    provide: ErrorHandler,
    useClass: BmErrorHandler,
  });
}

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
  overrides = <any>{
    swipe: { velocity: 0.4, threshold: 20 }, // override default settings
  };

  buildHammer(element: HTMLElement): any {
    let options = {};

    if (element.attributes['data-mc-options']) {
      try {
        const parseOptions = JSON.parse(
          element.attributes['data-mc-options'].nodeValue,
        );
        options = parseOptions;
      } catch (error) {
        console.error(
          'An error occurred when attempting to parse Hammer.js options:',
          error,
        );
      }
    }

    const mc = new Hammer(element, options);
    mc.get('pinch').set({ enable: true });
    mc.get('rotate').set({ enable: true });

    // eslint-disable-next-line guard-for-in
    for (const eventName in this.overrides) {
      mc.get(eventName).set(this.overrides[eventName]);
    }
    return mc;
  }
}

@NgModule({
  declarations: [AppComponent, PageNotFoundComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'server-btr-consumer' }),
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    CoreModule,
    // SocialLoginModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState],
      },
    }),
    LazySideMapComponent,
    SidenavComponent,
    BtBreadcrumbsComponent,
    MatIconModule,
    MatSnackBarModule,
  ],
  providers: [
    ...SHARED_INTERCEPTORS,
    // {
    //   provide: RouteReuseStrategy,
    //   useClass: BmRouteReuseStrategyService
    // },
    Title,
    {
      provide: AmplitudeAbstractService,
      useClass: AmplitudeBrowserService,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: init_app,
      deps: [AppLoadService],
      multi: true,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig,
    },

    ...OPT_PROVIDERS,
    IsPlacesVisibleGuard,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
